import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import toast from "react-hot-toast";
import {
    FiHome, FiUsers, FiBriefcase, FiDollarSign, FiCheckSquare,
    FiRefreshCw, FiLogOut, FiSettings, FiUserPlus, FiGrid,
    FiArrowRight, FiLayers, FiTruck, FiAlertTriangle,
    FiSend, FiActivity, FiAward, FiBookOpen,
    FiCheckCircle, FiCreditCard, FiBarChart2, FiList, FiShield,
    FiCopy, FiCheck, FiX, FiEyeOff, FiEye
} from "react-icons/fi";
import {
    ResponsiveContainer, BarChart, Bar, AreaChart, Area, PieChart, Pie, Cell,
    XAxis, YAxis, Tooltip, Legend, CartesianGrid
} from "recharts";
import { useApp } from "../../context/AppContext";
import dashboardService from "../../services/dashboardService";
import escalationService from "../../services/escalationService";
import userService from "../../services/userService";

// ── Currency and Number Formatters ───────────────────────────────────────────
const inr = (n) => `₹ ${Number(n || 0).toLocaleString("en-IN")}`;
const inrShort = (v) => {
    const n = Number(v || 0);
    if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)} Cr`;
    if (n >= 100000) return `₹${(n / 100000).toFixed(1)} L`;
    if (n >= 1000) return `₹${(n / 1000).toFixed(0)} K`;
    return `₹${n}`;
};

// ── Palette for charts ───────────────────────────────────────────────────────
const PIE_COLORS = ["#059669", "#003b67", "#7c3aed", "#d97706", "#dc2626", "#0284c7", "#64748b"];

// ── StatCard Component ──────────────────────────────────────────────────────
function ExecutiveStatCard({ icon: Icon, label, value, accent, sub, badge, to }) {
    const cardContent = (
        <div
            className="card"
            style={{
                padding: "1.2rem 1.3rem",
                borderLeft: `4px solid ${accent}`,
                position: "relative",
                overflow: "hidden",
                boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                transition: "all 0.2s ease",
                cursor: to ? "pointer" : "default",
                background: "#ffffff",
                borderRadius: "10px",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between"
            }}
            onMouseEnter={(e) => {
                if (to) {
                    e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,0.09)";
                    e.currentTarget.style.transform = "translateY(-2px)";
                }
            }}
            onMouseLeave={(e) => {
                if (to) {
                    e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.04)";
                    e.currentTarget.style.transform = "translateY(0)";
                }
            }}
        >
            <div style={{ position: "absolute", top: 8, right: 12, opacity: 0.05, pointerEvents: "none" }}>
                <Icon size={64} color={accent} />
            </div>

            <div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.55rem" }}>
                        <span style={{
                            width: 32, height: 32, borderRadius: 8,
                            background: `${accent}15`, display: "flex", alignItems: "center", justifyContent: "center"
                        }}>
                            <Icon size={16} color={accent} />
                        </span>
                        <span style={{ fontSize: 11.5, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--text-muted)", fontWeight: 700 }}>
                            {label}
                        </span>
                    </div>
                    {badge && (
                        <span style={{
                            fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 20,
                            background: `${accent}15`, color: accent
                        }}>
                            {badge}
                        </span>
                    )}
                </div>

                <div style={{ fontSize: 24, fontWeight: 800, color: "#0f172a", letterSpacing: "-0.02em" }}>
                    {value}
                </div>
            </div>

            {sub && (
                <div style={{ fontSize: 12, color: "#64748b", marginTop: 8, display: "flex", alignItems: "center", gap: 4 }}>
                    {sub}
                </div>
            )}
        </div>
    );

    return to ? <Link to={to} style={{ textDecoration: "none", color: "inherit" }}>{cardContent}</Link> : cardContent;
}

// ── Custom Tooltip for Charts ────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    return (
        <div style={{
            background: "#ffffff",
            padding: "10px 14px",
            border: "1px solid #e2e8f0",
            borderRadius: 8,
            boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
            fontSize: 12.5
        }}>
            <div style={{ fontWeight: 700, marginBottom: 6, color: "#1e293b" }}>{label}</div>
            {payload.map((entry, index) => (
                <div key={`tooltip-${index}`} style={{ display: "flex", alignItems: "center", gap: 6, color: entry.color || "#003b67", margin: "3px 0" }}>
                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: entry.color || "#003b67" }} />
                    <span style={{ color: "#64748b" }}>{entry.name}:</span>
                    <strong>{typeof entry.value === "number" && entry.value > 1000 ? inr(entry.value) : entry.value}</strong>
                </div>
            ))}
        </div>
    );
};

export default function SuperAdminDashboard() {
    const { user, logout, selectedCollegeId, selectCollege } = useApp();
    const navigate = useNavigate();

    // ── State ────────────────────────────────────────────────────────────────
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    // Filters — initialize from global AppContext selectedCollegeId
    const [selectedCollege, setSelectedCollege] = useState(() => selectedCollegeId || "all");
    const [period, setPeriod] = useState("session"); // "session" | "today" | "week" | "month" | "custom"
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");

    // Sync when context changes from Header or elsewhere
    useEffect(() => {
        if (selectedCollegeId) {
            setSelectedCollege(selectedCollegeId);
        }
    }, [selectedCollegeId]);

    const handleCollegeChange = (newCol) => {
        setSelectedCollege(newCol);
        if (selectCollege) {
            selectCollege(newCol);
        }
    };

    // Helper to append ?college=... when a specific college is active
    const withCollege = (path) => {
        if (!selectedCollege || selectedCollege === "all") return path;
        const sep = path.includes("?") ? "&" : "?";
        return `${path}${sep}college=${selectedCollege}`;
    };

    // Escalations
    const [escalationStats, setEscalationStats] = useState(null);
    const [pendingEscalations, setPendingEscalations] = useState([]);

    // Create Admin Modal State
    const [adminModalOpen, setAdminModalOpen] = useState(false);
    const [preSelectedAdminCol, setPreSelectedAdminCol] = useState("");

    const handleOpenCreateAdmin = (colId) => {
        setPreSelectedAdminCol(colId || (selectedCollege !== "all" ? selectedCollege : ""));
        setAdminModalOpen(true);
    };

    // ── Data Loading ─────────────────────────────────────────────────────────
    const loadDashboard = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (selectedCollege && selectedCollege !== "all") {
                params.college = selectedCollege;
            }
            if (period && period !== "session") {
                params.period = period;
            }
            if (period === "custom") {
                if (startDate) params.startDate = startDate;
                if (endDate) params.endDate = endDate;
            }

            const res = await dashboardService.getSuperAdminSummary(params);
            if (res.data?.success) {
                setData(res.data.data);
            } else if (res.data) {
                setData(res.data);
            }
        } catch (err) {
            console.error("Error loading Super Admin dashboard:", err);
            toast.error(err.response?.data?.message || err.message || "Failed to load dashboard data");
        } finally {
            setLoading(false);
        }
    }, [selectedCollege, period, startDate, endDate]);

    // Load escalations
    const loadEscalations = useCallback(async () => {
        try {
            const [statsRes, listRes] = await Promise.all([
                escalationService.getStats(),
                escalationService.getAll({ limit: 5, status: "pending" })
            ]);
            if (statsRes.success) setEscalationStats(statsRes.stats);
            if (listRes.success) setPendingEscalations(listRes.data || []);
        } catch (err) {
            console.error("Error loading escalations:", err);
        }
    }, []);

    useEffect(() => {
        loadDashboard();
    }, [loadDashboard]);

    useEffect(() => {
        loadEscalations();
    }, [loadEscalations]);

    const handleRefresh = async () => {
        setRefreshing(true);
        await Promise.all([loadDashboard(), loadEscalations()]);
        setRefreshing(false);
        toast.success("Dashboard data refreshed");
    };

    const handleLogout = () => {
        logout();
        navigate("/superadmin/login", { replace: true });
    };

    // ── Computed Values ──────────────────────────────────────────────────────
    const colleges = data?.colleges || [];
    const kpis = data?.kpis || {};
    const financials = data?.financials || {};
    const attendanceToday = data?.attendanceToday || {};
    const collegeMatrix = data?.collegeMatrix || [];
    const exceptions = data?.exceptions || {};
    const academics = data?.academics || {};
    const scholarships = data?.scholarships || {};
    const hostel = data?.hostel || {};
    const transport = data?.transport || {};

    // Payment modes pie chart data
    const paymentModesPieData = useMemo(() => {
        const modes = financials.paymentModes || {};
        return Object.entries(modes)
            .filter(([_, val]) => Number(val) > 0)
            .map(([key, val]) => ({ name: key, value: Number(val) }));
    }, [financials.paymentModes]);

    // Has any urgent exceptions
    const hasExceptions = Boolean(
        (exceptions.cashShortages && exceptions.cashShortages.length > 0) ||
        (exceptions.delayedDeposits && exceptions.delayedDeposits.length > 0) ||
        (exceptions.lowAttendanceColleges && exceptions.lowAttendanceColleges.length > 0) ||
        (exceptions.lowSyllabusColleges && exceptions.lowSyllabusColleges.length > 0) ||
        (exceptions.urgentEscalationsCount && exceptions.urgentEscalationsCount > 0)
    );

    return (
        <div style={{ minHeight: "100vh", background: "#f8fafc", color: "#1e293b" }}>
            {/* ── Executive Header ─────────────────────────────────────────── */}
            <header style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "1rem 2rem", background: "linear-gradient(135deg, #0F172A 0%, #1E293B 100%)",
                color: "#fff", borderBottom: "1px solid rgba(255,255,255,0.1)",
                position: "sticky", top: 0, zIndex: 100, backdropFilter: "blur(8px)"
            }}>
                <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                    <span style={{
                        width: 40, height: 40, borderRadius: 10,
                        background: "linear-gradient(135deg, #B08D45 0%, #D4AF37 100%)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        boxShadow: "0 4px 12px rgba(176,141,69,0.35)", color: "#fff"
                    }}>
                        <FiGrid size={20} />
                    </span>
                    <div>
                        <div style={{ fontWeight: 800, fontSize: 16, letterSpacing: "-0.01em", display: "flex", alignItems: "center", gap: 8 }}>
                            Central India Group of Institutions
                            <span style={{
                                fontSize: 10, textTransform: "uppercase", letterSpacing: "0.08em",
                                background: "rgba(176,141,69,0.25)", color: "#f7d583", padding: "2px 8px",
                                borderRadius: 20, border: "1px solid rgba(176,141,69,0.4)"
                            }}>
                                Super Admin Executive Cockpit
                            </span>
                        </div>
                        <div style={{ fontSize: 11.5, color: "#94a3b8" }}>
                            Consolidated Group-Level Governance, Academic, Attendance &amp; Financial Overview
                        </div>
                    </div>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                    <div style={{ textAlign: "right" }}>
                        <div style={{ fontSize: 13, fontWeight: 700 }}>{user?.displayName || user?.username || "Management"}</div>
                        <div style={{ fontSize: 11, color: "#94a3b8" }}>Group Administrator</div>
                    </div>

                    <button
                        onClick={handleLogout}
                        style={{
                            display: "flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,0.08)",
                            border: "1px solid rgba(255,255,255,0.2)", color: "#fff", borderRadius: 8,
                            padding: "7px 12px", fontSize: 12.5, fontWeight: 600, cursor: "pointer",
                            transition: "all 0.15s ease"
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = "rgba(255,255,255,0.15)"}
                        onMouseLeave={(e) => e.currentTarget.style.background = "rgba(255,255,255,0.08)"}
                    >
                        <FiLogOut size={14} /> Logout
                    </button>
                </div>
            </header>

            <div style={{ maxWidth: "100%", margin: "0 auto", padding: "1.75rem" }}>
                {/* ── Control Bar: College & Period Filtering ───────────────── */}
                <div className="card" style={{
                    padding: "1rem 1.5rem", marginBottom: "1.5rem", borderRadius: 12,
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    flexWrap: "wrap", gap: "1rem", border: "1px solid #e2e8f0",
                    background: "#ffffff"
                }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "1rem", flexWrap: "wrap" }}>
                        {/* College Selector */}
                        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                            <span style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", color: "var(--text-muted)", display: "flex", alignItems: "center", gap: 4 }}>
                                <FiHome size={13} /> Campus:
                            </span>
                            <select
                                value={selectedCollege}
                                onChange={(e) => handleCollegeChange(e.target.value)}
                                className="form-control"
                                style={{ minWidth: 260, fontSize: 13, fontWeight: 600, color: "#003b67", borderRadius: 8 }}
                            >
                                <option value="all">🌐 All Colleges (Group Consolidated)</option>
                                {colleges.map((c) => (
                                    <option key={c._id} value={c._id}>
                                        {c.name} ({c.code})
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Period Filter Tabs */}
                        <div style={{ display: "flex", alignItems: "center", gap: "0.3rem", background: "#f1f5f9", padding: 3, borderRadius: 8 }}>
                            {[
                                { key: "session", label: "🌟 Full Session" },
                                { key: "today", label: "📅 Today" },
                                { key: "week", label: "🗓 This Week" },
                                { key: "month", label: "📆 This Month" },
                                { key: "custom", label: "⚙️ Custom Range" },
                            ].map((p) => (
                                <button
                                    key={p.key}
                                    type="button"
                                    onClick={() => setPeriod(p.key)}
                                    style={{
                                        border: "none",
                                        padding: "6px 12px",
                                        borderRadius: 6,
                                        fontSize: 12,
                                        fontWeight: period === p.key ? 700 : 500,
                                        background: period === p.key ? "#ffffff" : "transparent",
                                        color: period === p.key ? "#003b67" : "#64748b",
                                        boxShadow: period === p.key ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
                                        cursor: "pointer",
                                        transition: "all 0.15s ease"
                                    }}
                                >
                                    {p.label}
                                </button>
                            ))}
                        </div>

                        {/* Custom Date Inputs */}
                        {period === "custom" && (
                            <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                                <input
                                    type="date"
                                    className="form-control"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    style={{ fontSize: 12, padding: "5px 8px" }}
                                />
                                <span style={{ fontSize: 12, color: "var(--text-muted)" }}>to</span>
                                <input
                                    type="date"
                                    className="form-control"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    style={{ fontSize: 12, padding: "5px 8px" }}
                                />
                            </div>
                        )}
                    </div>

                    {/* Right side controls */}
                    <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                        <span style={{ fontSize: 12, color: "#64748b", display: "flex", alignItems: "center", gap: 5 }}>
                            <FiCheckCircle size={13} color="#059669" /> Live MongoDB Data
                        </span>
                        <button
                            className="btn btn-outline"
                            onClick={handleRefresh}
                            disabled={refreshing || loading}
                            style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem", fontSize: 12.5, padding: "6px 12px", borderRadius: 8 }}
                        >
                            <FiRefreshCw size={13} style={{ animation: refreshing ? "spin 0.7s linear infinite" : "none" }} />
                            Refresh
                        </button>
                    </div>
                </div>

                {/* ── Management Alert & Exceptions Ribbon ─────────────────── */}
                {hasExceptions && (
                    <div style={{
                        marginBottom: "1.5rem", borderRadius: 10, padding: "0.9rem 1.25rem",
                        background: "#fffbeb", border: "1px solid #fde68a",
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                        flexWrap: "wrap", gap: "0.75rem"
                    }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
                            <span style={{
                                width: 28, height: 28, borderRadius: "50%", background: "#fef3c7",
                                display: "flex", alignItems: "center", justifyContent: "center", color: "#d97706"
                            }}>
                                <FiAlertTriangle size={16} />
                            </span>
                            <div style={{ fontWeight: 700, fontSize: 13, color: "#92400e" }}>
                                Attention Required by Group Management:
                            </div>

                            <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                                {exceptions.cashShortages?.length > 0 && (
                                    <span style={{ fontSize: 11.5, fontWeight: 700, padding: "3px 8px", borderRadius: 6, background: "#fee2e2", color: "#991b1b" }}>
                                        ⚠️ {exceptions.cashShortages.length} Clerk Cash Shortage{exceptions.cashShortages.length > 1 ? "s" : ""}
                                    </span>
                                )}
                                {exceptions.delayedDeposits?.length > 0 && (
                                    <span style={{ fontSize: 11.5, fontWeight: 700, padding: "3px 8px", borderRadius: 6, background: "#fef3c7", color: "#92400e" }}>
                                        ⏳ {exceptions.delayedDeposits.length} Delayed Bank Deposit{exceptions.delayedDeposits.length > 1 ? "s" : ""}
                                    </span>
                                )}
                                {exceptions.lowAttendanceColleges?.length > 0 && (
                                    <span style={{ fontSize: 11.5, fontWeight: 700, padding: "3px 8px", borderRadius: 6, background: "#ffedd5", color: "#c2410c" }}>
                                        📉 {exceptions.lowAttendanceColleges.length} Campus Below 75% Attendance
                                    </span>
                                )}
                                {exceptions.urgentEscalationsCount > 0 && (
                                    <span style={{ fontSize: 11.5, fontWeight: 700, padding: "3px 8px", borderRadius: 6, background: "#f5f3ff", color: "#6b21a8" }}>
                                        🚨 {exceptions.urgentEscalationsCount} Urgent Escalation Ticket{exceptions.urgentEscalationsCount > 1 ? "s" : ""}
                                    </span>
                                )}
                            </div>
                        </div>

                        <div style={{ display: "flex", gap: "0.5rem" }}>
                            <Link to={withCollege("/superadmin/cash-radar")} className="btn btn-sm btn-outline" style={{ fontSize: 11.5, padding: "4px 8px" }}>
                                View Cash Radar <FiArrowRight size={11} />
                            </Link>
                            <Link to={withCollege("/escalations")} className="btn btn-sm btn-primary" style={{ fontSize: 11.5, padding: "4px 8px" }}>
                                Review Escalations <FiArrowRight size={11} />
                            </Link>
                        </div>
                    </div>
                )}

                {loading ? (
                    <div className="loading-center" style={{ minHeight: 320, padding: "3rem", background: "#ffffff", borderRadius: 12 }}>
                        <div className="spinner" />
                        <div style={{ marginTop: 12, fontWeight: 600, color: "#64748b" }}>
                            Aggregating real multi-college records across database...
                        </div>
                    </div>
                ) : (
                    <>
                        {/* ── Top Executive KPI Stat Cards ───────────────────────── */}
                        <div style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                            gap: "1rem",
                            marginBottom: "1.5rem"
                        }}>
                            <ExecutiveStatCard
                                icon={FiUsers}
                                label="Active Students"
                                value={(kpis.totalActiveStudents || 0).toLocaleString("en-IN")}
                                accent="#003b67"
                                badge={`${kpis.totalColleges || colleges.length} Colleges`}
                                sub={`+${kpis.totalNewAdmissions || 0} new admissions this session`}
                                to={withCollege("/students")}
                            />

                            <ExecutiveStatCard
                                icon={FiBriefcase}
                                label="Total Employees"
                                value={(kpis.totalEmployees || 0).toLocaleString("en-IN")}
                                accent="#7c3aed"
                                badge="Faculty & Staff"
                                sub={`Teaching: ${kpis.teachingStaff || 0} · Non-Teaching: ${kpis.nonTeachingStaff || 0}`}
                                to={withCollege("/employees")}
                            />

                            <ExecutiveStatCard
                                icon={FiDollarSign}
                                label="Fees Collected"
                                value={inrShort(kpis.feesCollected)}
                                accent="#059669"
                                badge={`${kpis.collectionPercentage || 0}% Collected`}
                                sub={`Total decided: ${inrShort(kpis.feesReceivable)}`}
                                to={withCollege("/fees")}
                            />

                            <ExecutiveStatCard
                                icon={FiAlertTriangle}
                                label="Pending Fees"
                                value={inrShort(kpis.feesPending)}
                                accent="#dc2626"
                                badge={`${100 - (kpis.collectionPercentage || 0)}% Outstanding`}
                                sub="Defaulter balances across all courses"
                                to={withCollege("/fees/pending")}
                            />

                            <ExecutiveStatCard
                                icon={FiCheckSquare}
                                label="Student Attendance"
                                value={`${kpis.studentAttendancePercentage || 0}%`}
                                accent="#B08D45"
                                badge="Today"
                                sub={`${(kpis.studentAttendancePresent || 0).toLocaleString("en-IN")} present of ${(kpis.studentAttendanceMarked || 0).toLocaleString("en-IN")} marked`}
                                to={withCollege("/attendance/report")}
                            />

                            <ExecutiveStatCard
                                icon={FiActivity}
                                label="Cash In Vaults"
                                value={inr(kpis.cashInVault)}
                                accent="#d97706"
                                badge={kpis.totalClerkShortage > 0 ? `₹${kpis.totalClerkShortage} Short` : "Balanced"}
                                sub="Physical currency awaiting bank deposit"
                                to={withCollege("/superadmin/cash-radar")}
                            />
                        </div>

                        {/* ── High Impact Visualizations (Charts Grid) ──────────── */}
                        <div style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(480px, 1fr))",
                            gap: "1.25rem",
                            marginBottom: "1.5rem"
                        }}>
                            {/* Chart 1: Fee Collection & Pending by College */}
                            <div className="card" style={{ padding: "1.3rem", borderRadius: 12, border: "1px solid #e2e8f0" }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
                                    <div>
                                        <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>
                                            Fee Collection vs Outstanding by Campus
                                        </div>
                                        <div style={{ fontSize: 11.5, color: "#64748b" }}>
                                            Comparing fees received vs pending dues across institutes
                                        </div>
                                    </div>
                                    <span style={{ fontSize: 12, fontWeight: 700, color: "#059669", background: "#ecfdf5", padding: "3px 8px", borderRadius: 6 }}>
                                        Total: {inrShort(kpis.feesCollected)}
                                    </span>
                                </div>

                                <ResponsiveContainer width="100%" height={260}>
                                    <BarChart data={financials.collegeCollectionChart || []} margin={{ top: 10, right: 10, left: 0, bottom: 20 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                                        <XAxis dataKey="name" fontSize={12} tickLine={false} />
                                        <YAxis fontSize={11} tickFormatter={(v) => inrShort(v)} tickLine={false} axisLine={false} />
                                        <Tooltip content={<CustomTooltip />} />
                                        <Legend wrapperStyle={{ fontSize: 12, paddingTop: 6 }} />
                                        <Bar dataKey="collected" name="Collected Fees" fill="#059669" radius={[4, 4, 0, 0]} maxBarSize={40} />
                                        <Bar dataKey="pending" name="Pending Fees" fill="#f87171" radius={[4, 4, 0, 0]} maxBarSize={40} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>

                            {/* Chart 2: Payment Modes Distribution */}
                            <div className="card" style={{ padding: "1.3rem", borderRadius: 12, border: "1px solid #e2e8f0" }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
                                    <div>
                                        <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>
                                            Payment Methods &amp; Cash Ratio
                                        </div>
                                        <div style={{ fontSize: 11.5, color: "#64748b" }}>
                                            Physical cash vs digital payments (UPI, NEFT, Cheque, DD)
                                        </div>
                                    </div>
                                    <div style={{ fontSize: 11.5, fontWeight: 700 }}>
                                        Cash: <strong style={{ color: "#d97706" }}>{inrShort(financials.cashVsDigital?.physicalCash || 0)}</strong>
                                    </div>
                                </div>

                                <div style={{ display: "flex", alignItems: "center", gap: "1rem", flexWrap: "wrap" }}>
                                    <div style={{ flex: "1 1 220px", height: 260 }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <Pie
                                                    data={paymentModesPieData.length ? paymentModesPieData : [{ name: "No data", value: 1 }]}
                                                    cx="50%"
                                                    cy="50%"
                                                    innerRadius={60}
                                                    outerRadius={95}
                                                    paddingAngle={3}
                                                    dataKey="value"
                                                >
                                                    {paymentModesPieData.map((entry, index) => (
                                                        <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                                                    ))}
                                                </Pie>
                                                <Tooltip formatter={(v) => inr(v)} />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </div>

                                    {/* Pie Chart Legend with exact amounts */}
                                    <div style={{ flex: "1 1 180px", display: "grid", gap: "0.5rem" }}>
                                        {paymentModesPieData.map((entry, idx) => (
                                            <div key={entry.name} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12.5 }}>
                                                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                                    <span style={{ width: 10, height: 10, borderRadius: 3, background: PIE_COLORS[idx % PIE_COLORS.length] }} />
                                                    <span style={{ color: "#475569" }}>{entry.name}</span>
                                                </div>
                                                <strong style={{ color: "#0f172a" }}>{inrShort(entry.value)}</strong>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Chart 3: Monthly Collection Velocity / Trend */}
                            <div className="card" style={{ padding: "1.3rem", borderRadius: 12, border: "1px solid #e2e8f0" }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
                                    <div>
                                        <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>
                                            Monthly Revenue Collection Velocity
                                        </div>
                                        <div style={{ fontSize: 11.5, color: "#64748b" }}>
                                            Fee inflow volume timeline across academic calendar
                                        </div>
                                    </div>
                                    <span style={{ fontSize: 12, fontWeight: 600, color: "#003b67" }}>
                                        Trend History
                                    </span>
                                </div>

                                <ResponsiveContainer width="100%" height={230}>
                                    <AreaChart data={financials.monthlyTrend || []} margin={{ top: 10, right: 10, left: 0, bottom: 20 }}>
                                        <defs>
                                            <linearGradient id="feeGradient" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#003b67" stopOpacity={0.4} />
                                                <stop offset="95%" stopColor="#003b67" stopOpacity={0.0} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                                        <XAxis dataKey="name" fontSize={11} tickLine={false} />
                                        <YAxis fontSize={11} tickFormatter={(v) => inrShort(v)} tickLine={false} axisLine={false} />
                                        <Tooltip content={<CustomTooltip />} />
                                        <Area type="monotone" dataKey="fees" name="Collected" stroke="#003b67" strokeWidth={2.5} fillOpacity={1} fill="url(#feeGradient)" />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>

                            {/* Chart 4: Campus Student & Staff Attendance Comparison */}
                            <div className="card" style={{ padding: "1.3rem", borderRadius: 12, border: "1px solid #e2e8f0" }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
                                    <div>
                                        <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>
                                            Campus Attendance Benchmarking (Today)
                                        </div>
                                        <div style={{ fontSize: 11.5, color: "#64748b" }}>
                                            Student attendance % vs Employee attendance % by college
                                        </div>
                                    </div>
                                    <span style={{ fontSize: 11.5, fontWeight: 700, color: "#B08D45", background: "#fefce8", padding: "3px 8px", borderRadius: 6 }}>
                                        Target: 85%+
                                    </span>
                                </div>

                                <ResponsiveContainer width="100%" height={230}>
                                    <BarChart data={attendanceToday.chartData || []} margin={{ top: 10, right: 10, left: 0, bottom: 20 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                                        <XAxis dataKey="name" fontSize={12} tickLine={false} />
                                        <YAxis domain={[0, 100]} fontSize={11} tickFormatter={(v) => `${v}%`} tickLine={false} axisLine={false} />
                                        <Tooltip formatter={(v) => `${v}%`} />
                                        <Legend wrapperStyle={{ fontSize: 12, paddingTop: 6 }} />
                                        <Bar dataKey="studentAttendance" name="Student Attendance %" fill="#B08D45" radius={[4, 4, 0, 0]} maxBarSize={35} />
                                        <Bar dataKey="employeeAttendance" name="Staff Attendance %" fill="#0d9488" radius={[4, 4, 0, 0]} maxBarSize={35} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        {/* ── Group Fees & Financial Management Hub ─────────────── */}
                        <div className="card" style={{ marginBottom: "1.5rem", borderRadius: 12, overflow: "hidden", border: "1px solid #e2e8f0", background: "#ffffff" }}>
                            <div style={{
                                padding: "1.1rem 1.4rem",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                flexWrap: "wrap",
                                gap: "0.75rem",
                                background: "#ffffff",
                                borderBottom: "1px solid #e2e8f0"
                            }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
                                    <span style={{
                                        width: 34, height: 34, borderRadius: 8,
                                        background: "#05966915", display: "flex", alignItems: "center", justifyContent: "center",
                                        color: "#059669"
                                    }}>
                                        <FiCreditCard size={18} />
                                    </span>
                                    <div>
                                        <div style={{ fontSize: 15, fontWeight: 800, color: "#0f172a" }}>
                                            Group Fees &amp; Financial Management Hub
                                        </div>
                                        <div style={{ fontSize: 12, color: "#64748b" }}>
                                            Manage tuition structures, receipts, pending balances, institutional charges, fines &amp; transport across all colleges
                                        </div>
                                    </div>
                                </div>

                                <div style={{ display: "flex", gap: "0.6rem", flexWrap: "wrap" }}>
                                    <Link
                                        to={withCollege("/superadmin/cash-radar")}
                                        className="btn btn-sm btn-outline"
                                        style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5, fontWeight: 600, borderRadius: 8, padding: "6px 12px" }}
                                    >
                                        <FiActivity size={14} color="#003b67" /> Campus Cash Radar
                                    </Link>
                                    <Link
                                        to={withCollege("/fees")}
                                        className="btn btn-sm btn-primary"
                                        style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5, fontWeight: 600, borderRadius: 8, padding: "6px 12px", background: "#003b67" }}
                                    >
                                        <FiBarChart2 size={14} /> Open Fees Dashboard <FiArrowRight size={13} />
                                    </Link>
                                </div>
                            </div>

                            <div style={{
                                padding: "1.25rem",
                                display: "grid",
                                gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                                gap: "1rem"
                            }}>
                                {/* Card 1: Fees Overview Dashboard */}
                                <Link to={withCollege("/fees")} style={{ textDecoration: "none", color: "inherit" }}>
                                    <div
                                        style={{
                                            padding: "1rem", borderRadius: 10, border: "1px solid #e2e8f0",
                                            background: "#ffffff", transition: "all 0.2s ease", cursor: "pointer",
                                            height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between"
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.boxShadow = "0 6px 18px rgba(0,0,0,0.06)";
                                            e.currentTarget.style.borderColor = "#0284c7";
                                            e.currentTarget.style.transform = "translateY(-2px)";
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.boxShadow = "none";
                                            e.currentTarget.style.borderColor = "#e2e8f0";
                                            e.currentTarget.style.transform = "translateY(0)";
                                        }}
                                    >
                                        <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "0.4rem" }}>
                                            <span style={{ width: 28, height: 28, borderRadius: 6, background: "#0284c715", display: "flex", alignItems: "center", justifyContent: "center", color: "#0284c7" }}>
                                                <FiBarChart2 size={15} />
                                            </span>
                                            <div style={{ fontWeight: 700, fontSize: 14, color: "#1e293b" }}>Fees Overview Dashboard</div>
                                        </div>
                                        <div style={{ fontSize: 12, color: "#64748b", lineHeight: 1.45 }}>
                                            Collection rates, revenue charts, and fee head analytics across colleges &amp; courses.
                                        </div>
                                    </div>
                                </Link>

                                {/* Card 2: All Fee Receipts */}
                                <Link to={withCollege("/fees/receipts")} style={{ textDecoration: "none", color: "inherit" }}>
                                    <div
                                        style={{
                                            padding: "1rem", borderRadius: 10, border: "1px solid #e2e8f0",
                                            background: "#ffffff", transition: "all 0.2s ease", cursor: "pointer",
                                            height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between"
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.boxShadow = "0 6px 18px rgba(0,0,0,0.06)";
                                            e.currentTarget.style.borderColor = "#16a34a";
                                            e.currentTarget.style.transform = "translateY(-2px)";
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.boxShadow = "none";
                                            e.currentTarget.style.borderColor = "#e2e8f0";
                                            e.currentTarget.style.transform = "translateY(0)";
                                        }}
                                    >
                                        <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "0.4rem" }}>
                                            <span style={{ width: 28, height: 28, borderRadius: 6, background: "#16a34a15", display: "flex", alignItems: "center", justifyContent: "center", color: "#16a34a" }}>
                                                <FiList size={15} />
                                            </span>
                                            <div style={{ fontWeight: 700, fontSize: 14, color: "#1e293b" }}>All Fee Receipts</div>
                                        </div>
                                        <div style={{ fontSize: 12, color: "#64748b", lineHeight: 1.45 }}>
                                            Complete ledger of all issued fee receipts with print, reprint, and mode filtering.
                                        </div>
                                    </div>
                                </Link>

                                {/* Card 3: Pending Tuition Fees */}
                                <Link to={withCollege("/fees/pending")} style={{ textDecoration: "none", color: "inherit" }}>
                                    <div
                                        style={{
                                            padding: "1rem", borderRadius: 10, border: "1px solid #e2e8f0",
                                            background: "#ffffff", transition: "all 0.2s ease", cursor: "pointer",
                                            height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between"
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.boxShadow = "0 6px 18px rgba(0,0,0,0.06)";
                                            e.currentTarget.style.borderColor = "#dc2626";
                                            e.currentTarget.style.transform = "translateY(-2px)";
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.boxShadow = "none";
                                            e.currentTarget.style.borderColor = "#e2e8f0";
                                            e.currentTarget.style.transform = "translateY(0)";
                                        }}
                                    >
                                        <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "0.4rem" }}>
                                            <span style={{ width: 28, height: 28, borderRadius: 6, background: "#dc262615", display: "flex", alignItems: "center", justifyContent: "center", color: "#dc2626" }}>
                                                <FiAlertTriangle size={15} />
                                            </span>
                                            <div style={{ fontWeight: 700, fontSize: 14, color: "#1e293b" }}>Pending Tuition Fees</div>
                                        </div>
                                        <div style={{ fontSize: 12, color: "#64748b", lineHeight: 1.45 }}>
                                            Outstanding tuition fee balances, defaulter lists, and installment tracking.
                                        </div>
                                    </div>
                                </Link>

                                {/* Card 4: Charges & Fines Hub */}
                                <Link to={withCollege("/fines")} style={{ textDecoration: "none", color: "inherit" }}>
                                    <div
                                        style={{
                                            padding: "1rem", borderRadius: 10, border: "1px solid #e2e8f0",
                                            background: "#ffffff", transition: "all 0.2s ease", cursor: "pointer",
                                            height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between"
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.boxShadow = "0 6px 18px rgba(0,0,0,0.06)";
                                            e.currentTarget.style.borderColor = "#7c3aed";
                                            e.currentTarget.style.transform = "translateY(-2px)";
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.boxShadow = "none";
                                            e.currentTarget.style.borderColor = "#e2e8f0";
                                            e.currentTarget.style.transform = "translateY(0)";
                                        }}
                                    >
                                        <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "0.4rem" }}>
                                            <span style={{ width: 28, height: 28, borderRadius: 6, background: "#7c3aed15", display: "flex", alignItems: "center", justifyContent: "center", color: "#7c3aed" }}>
                                                <FiShield size={15} />
                                            </span>
                                            <div style={{ fontWeight: 700, fontSize: 14, color: "#1e293b" }}>Charges &amp; Fines Hub</div>
                                        </div>
                                        <div style={{ fontSize: 12, color: "#64748b", lineHeight: 1.45 }}>
                                            Issue, track, and collect Uniform Fees (₹2,300), Lab Manuals, Books, Breakage &amp; Library fines.
                                        </div>
                                    </div>
                                </Link>

                                {/* Card 5: Bus / Transport */}
                                <Link to={withCollege("/transport")} style={{ textDecoration: "none", color: "inherit" }}>
                                    <div
                                        style={{
                                            padding: "1rem", borderRadius: 10, border: "1px solid #e2e8f0",
                                            background: "#ffffff", transition: "all 0.2s ease", cursor: "pointer",
                                            height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between"
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.boxShadow = "0 6px 18px rgba(0,0,0,0.06)";
                                            e.currentTarget.style.borderColor = "#d97706";
                                            e.currentTarget.style.transform = "translateY(-2px)";
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.boxShadow = "none";
                                            e.currentTarget.style.borderColor = "#e2e8f0";
                                            e.currentTarget.style.transform = "translateY(0)";
                                        }}
                                    >
                                        <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "0.4rem" }}>
                                            <span style={{ width: 28, height: 28, borderRadius: 6, background: "#d9770615", display: "flex", alignItems: "center", justifyContent: "center", color: "#d97706" }}>
                                                <FiTruck size={15} />
                                            </span>
                                            <div style={{ fontWeight: 700, fontSize: 14, color: "#1e293b" }}>Bus / Transport</div>
                                        </div>
                                        <div style={{ fontSize: 12, color: "#64748b", lineHeight: 1.45 }}>
                                            Bus routes, distance pricing slabs, stop configurations, and student bus pass allotments.
                                        </div>
                                    </div>
                                </Link>

                                {/* Card 6: Fee Structures Config */}
                                <Link to={withCollege("/settings/fee-structures")} style={{ textDecoration: "none", color: "inherit" }}>
                                    <div
                                        style={{
                                            padding: "1rem", borderRadius: 10, border: "1px solid #e2e8f0",
                                            background: "#ffffff", transition: "all 0.2s ease", cursor: "pointer",
                                            height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between"
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.boxShadow = "0 6px 18px rgba(0,0,0,0.06)";
                                            e.currentTarget.style.borderColor = "#003b67";
                                            e.currentTarget.style.transform = "translateY(-2px)";
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.boxShadow = "none";
                                            e.currentTarget.style.borderColor = "#e2e8f0";
                                            e.currentTarget.style.transform = "translateY(0)";
                                        }}
                                    >
                                        <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "0.4rem" }}>
                                            <span style={{ width: 28, height: 28, borderRadius: 6, background: "#003b6715", display: "flex", alignItems: "center", justifyContent: "center", color: "#003b67" }}>
                                                <FiLayers size={15} />
                                            </span>
                                            <div style={{ fontWeight: 700, fontSize: 14, color: "#1e293b" }}>Fee Structures Config</div>
                                        </div>
                                        <div style={{ fontSize: 12, color: "#64748b", lineHeight: 1.45 }}>
                                            Configure course-wise academic fee structures, category quotas (General/OBC/SC/ST), and heads.
                                        </div>
                                    </div>
                                </Link>

                                {/* Card 7: Campus Cash Radar Matrix */}
                                <Link to={withCollege("/superadmin/cash-radar")} style={{ textDecoration: "none", color: "inherit" }}>
                                    <div
                                        style={{
                                            padding: "1rem", borderRadius: 10, border: "1px solid #e2e8f0",
                                            background: "#ffffff", transition: "all 0.2s ease", cursor: "pointer",
                                            height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between"
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.boxShadow = "0 6px 18px rgba(0,0,0,0.06)";
                                            e.currentTarget.style.borderColor = "#0d9488";
                                            e.currentTarget.style.transform = "translateY(-2px)";
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.boxShadow = "none";
                                            e.currentTarget.style.borderColor = "#e2e8f0";
                                            e.currentTarget.style.transform = "translateY(0)";
                                        }}
                                    >
                                        <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "0.4rem" }}>
                                            <span style={{ width: 28, height: 28, borderRadius: 6, background: "#0d948815", display: "flex", alignItems: "center", justifyContent: "center", color: "#0d9488" }}>
                                                <FiActivity size={15} />
                                            </span>
                                            <div style={{ fontWeight: 700, fontSize: 14, color: "#1e293b" }}>Campus Cash Radar Matrix</div>
                                        </div>
                                        <div style={{ fontSize: 12, color: "#64748b", lineHeight: 1.45 }}>
                                            Cross-campus cash collection tracking, clerk shortages, vault holdings, and bank deposit delays.
                                        </div>
                                    </div>
                                </Link>
                            </div>
                        </div>

                        {/* ── Cross-College Performance & Comparison Matrix Table ── */}
                        <div className="card" style={{ marginBottom: "1.5rem", borderRadius: 12, overflow: "hidden", border: "1px solid #e2e8f0" }}>
                            <div style={{
                                padding: "1.1rem 1.4rem",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                flexWrap: "wrap",
                                gap: "0.75rem",
                                background: "#ffffff",
                                borderBottom: "1px solid #e2e8f0"
                            }}>
                                <div>
                                    <div style={{ fontSize: 15, fontWeight: 800, color: "#0f172a", display: "flex", alignItems: "center", gap: 8 }}>
                                        <FiGrid color="#003b67" size={17} /> Cross-College Performance &amp; Governance Matrix
                                    </div>
                                    <div style={{ fontSize: 12, color: "#64748b" }}>
                                        Comprehensive side-by-side comparison across all {colleges.length} institutional campuses
                                    </div>
                                </div>

                                {selectedCollege !== "all" && (
                                    <button
                                        className="btn btn-sm btn-outline"
                                        onClick={() => handleCollegeChange("all")}
                                        style={{ fontSize: 12, padding: "4px 10px", borderRadius: 6 }}
                                    >
                                        Show All Colleges
                                    </button>
                                )}
                            </div>

                            <div className="table-responsive">
                                <table className="table" style={{ width: "100%", margin: 0, fontSize: 13 }}>
                                    <thead style={{ background: "#f8fafc", borderBottom: "2px solid #e2e8f0" }}>
                                        <tr>
                                            <th>College / Campus</th>
                                            <th>Admin Account</th>
                                            <th>Discipline</th>
                                            <th style={{ textAlign: "right" }}>Students</th>
                                            <th style={{ textAlign: "right" }}>Staff</th>
                                            <th style={{ textAlign: "right" }}>Fees Receivable</th>
                                            <th style={{ textAlign: "right" }}>Collected</th>
                                            <th style={{ textAlign: "right" }}>Pending</th>
                                            <th style={{ textAlign: "center" }}>Collection %</th>
                                            <th style={{ textAlign: "right" }}>Student Att.</th>
                                            <th style={{ textAlign: "right" }}>Syllabus</th>
                                            <th style={{ textAlign: "center" }}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {collegeMatrix.map((c) => {
                                            const isSelected = selectedCollege === c.collegeId;
                                            return (
                                                <tr key={c.collegeId} style={{ background: isSelected ? "#f0f9ff" : "inherit" }}>
                                                    <td style={{ fontWeight: 700 }}>
                                                        <div style={{ color: "#0f172a" }}>{c.name}</div>
                                                        <div style={{ display: "flex", gap: 6, marginTop: 2 }}>
                                                            <span className="badge badge-info" style={{ fontSize: 10 }}>{c.code}</span>
                                                            {c.city && <span style={{ fontSize: 11, color: "#64748b" }}>{c.city}</span>}
                                                        </div>
                                                    </td>
                                                    <td style={{ fontSize: 12 }}>
                                                        {c.adminUser ? (
                                                            <span style={{
                                                                display: "inline-flex", alignItems: "center", gap: 4,
                                                                background: "#ecfdf5", color: "#065f46", border: "1px solid #a7f3d0",
                                                                padding: "2px 7px", borderRadius: 6, fontWeight: 700, fontSize: 11
                                                            }}>
                                                                <FiShield size={11} color="#059669" /> @{c.adminUser.username}
                                                            </span>
                                                        ) : (
                                                            <button
                                                                className="btn btn-sm"
                                                                onClick={() => handleOpenCreateAdmin(c.collegeId)}
                                                                style={{
                                                                    fontSize: 10.5, padding: "2px 7px", borderRadius: 6,
                                                                    background: "#fffbeb", color: "#b45309", border: "1px solid #fde68a",
                                                                    display: "inline-flex", alignItems: "center", gap: 3, cursor: "pointer"
                                                                }}
                                                                title="Create Administrator account for this campus"
                                                            >
                                                                <FiUserPlus size={10} /> + Add Admin
                                                            </button>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <span style={{
                                                            fontSize: 11, fontWeight: 600, padding: "2px 7px", borderRadius: 4,
                                                            background: "#f1f5f9", color: "#334155"
                                                        }}>
                                                            {c.discipline || "General"}
                                                        </span>
                                                    </td>
                                                    <td style={{ textAlign: "right", fontWeight: 600 }}>
                                                        {c.students.toLocaleString("en-IN")}
                                                    </td>
                                                    <td style={{ textAlign: "right" }}>
                                                        <div><strong>{c.employees}</strong></div>
                                                        <div style={{ fontSize: 11, color: "#64748b" }}>{c.teachingStaff} teach</div>
                                                    </td>
                                                    <td style={{ textAlign: "right", fontWeight: 600 }}>
                                                        {inr(c.feesReceivable)}
                                                    </td>
                                                    <td style={{ textAlign: "right", fontWeight: 700, color: "#16a34a" }}>
                                                        {inr(c.feesCollected)}
                                                    </td>
                                                    <td style={{
                                                        textAlign: "right", fontWeight: 700,
                                                        color: c.feesPending > 200000 ? "#dc2626" : "inherit"
                                                    }}>
                                                        {inr(c.feesPending)}
                                                    </td>
                                                    <td style={{ textAlign: "center" }}>
                                                        <div style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "center" }}>
                                                            <span style={{
                                                                fontWeight: 700,
                                                                fontSize: 12,
                                                                color: c.collectionPercentage >= 75 ? "#16a34a" : c.collectionPercentage >= 50 ? "#d97706" : "#dc2626"
                                                            }}>
                                                                {c.collectionPercentage}%
                                                            </span>
                                                        </div>
                                                        <div style={{
                                                            width: 60, height: 4, borderRadius: 2, background: "#e2e8f0",
                                                            margin: "3px auto 0", overflow: "hidden"
                                                        }}>
                                                            <div style={{
                                                                height: "100%", width: `${c.collectionPercentage}%`,
                                                                background: c.collectionPercentage >= 75 ? "#16a34a" : c.collectionPercentage >= 50 ? "#d97706" : "#dc2626"
                                                            }} />
                                                        </div>
                                                    </td>
                                                    <td style={{ textAlign: "right" }}>
                                                        <span style={{
                                                            fontSize: 11.5, fontWeight: 700, padding: "2px 6px", borderRadius: 4,
                                                            background: c.studentAttendancePct >= 85 ? "#ecfdf5" : c.studentAttendancePct >= 75 ? "#fefce8" : "#fef2f2",
                                                            color: c.studentAttendancePct >= 85 ? "#065f46" : c.studentAttendancePct >= 75 ? "#854d0e" : "#991b1b"
                                                        }}>
                                                            {c.studentAttendanceMarked > 0 ? `${c.studentAttendancePct}%` : "Not Marked"}
                                                        </span>
                                                    </td>
                                                    <td style={{ textAlign: "right" }}>
                                                        <div style={{ fontWeight: 600 }}>{c.syllabusProgressPct}%</div>
                                                        <div style={{ fontSize: 10.5, color: "#64748b" }}>{c.trackedSubjects} subjects</div>
                                                    </td>
                                                    <td style={{ textAlign: "center" }}>
                                                        <div style={{ display: "flex", gap: 4, justifyContent: "center" }}>
                                                            <button
                                                                className={`btn btn-sm ${isSelected ? "btn-primary" : "btn-outline"}`}
                                                                onClick={() => handleCollegeChange(isSelected ? "all" : c.collegeId)}
                                                                style={{ padding: "3px 8px", fontSize: 11.5 }}
                                                                title="Focus dashboard on this college"
                                                            >
                                                                {isSelected ? "Reset" : "Focus"}
                                                            </button>
                                                            <Link
                                                                to={`/fees?college=${c.collegeId}`}
                                                                className="btn btn-sm btn-outline"
                                                                style={{ padding: "3px 6px", fontSize: 11 }}
                                                                title="Open Fee Dashboard for this college"
                                                            >
                                                                Fees
                                                            </Link>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* ── Operational & Academic Deep-Dive Hubs (Modular Cards) ── */}
                        <div style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
                            gap: "1.25rem",
                            marginBottom: "1.5rem"
                        }}>
                            {/* Academic & Syllabus Tracking Card */}
                            <div className="card" style={{ padding: "1.25rem", borderRadius: 10, border: "1px solid #e2e8f0" }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
                                        <span style={{ width: 30, height: 30, borderRadius: 8, background: "#003b6715", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                            <FiBookOpen size={16} color="#003b67" />
                                        </span>
                                        <div>
                                            <div style={{ fontWeight: 700, fontSize: 14 }}>Syllabus &amp; Academic Progress</div>
                                            <div style={{ fontSize: 11.5, color: "#64748b" }}>Curriculum delivery across group</div>
                                        </div>
                                    </div>
                                    <Link to={withCollege("/subjects/performance")} className="btn btn-sm btn-outline" style={{ fontSize: 11.5, padding: "3px 8px" }}>
                                        Tracker <FiArrowRight size={11} />
                                    </Link>
                                </div>

                                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem", marginBottom: 10 }}>
                                    <div style={{ padding: "0.6rem", background: "#f8fafc", borderRadius: 8, border: "1px solid #e2e8f0" }}>
                                        <div style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 700 }}>Avg Completion</div>
                                        <div style={{ fontSize: 20, fontWeight: 800, color: "#003b67", marginTop: 2 }}>{academics.averageCompletionRate || 0}%</div>
                                    </div>
                                    <div style={{ padding: "0.6rem", background: "#f8fafc", borderRadius: 8, border: "1px solid #e2e8f0" }}>
                                        <div style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 700 }}>Subjects Tracked</div>
                                        <div style={{ fontSize: 20, fontWeight: 800, color: "#0f172a", marginTop: 2 }}>{academics.totalTrackedSubjects || 0}</div>
                                    </div>
                                </div>

                                <div style={{ fontSize: 12, color: "#475569", display: "flex", justifyContent: "space-between", borderTop: "1px solid #f1f5f9", paddingTop: 8 }}>
                                    <span>Completed: <strong>{academics.completedSubjects || 0}</strong></span>
                                    <span>In Progress: <strong>{academics.inProgressSubjects || 0}</strong></span>
                                    <span>Not Started: <strong>{academics.notStartedSubjects || 0}</strong></span>
                                </div>
                            </div>

                            {/* Scholarships Overview Card */}
                            <div className="card" style={{ padding: "1.25rem", borderRadius: 10, border: "1px solid #e2e8f0" }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
                                        <span style={{ width: 30, height: 30, borderRadius: 8, background: "#7c3aed15", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                            <FiAward size={16} color="#7c3aed" />
                                        </span>
                                        <div>
                                            <div style={{ fontWeight: 700, fontSize: 14 }}>Government Scholarships</div>
                                            <div style={{ fontSize: 11.5, color: "#64748b" }}>Sanctioned &amp; Disbursed aid</div>
                                        </div>
                                    </div>
                                    <Link to={withCollege("/scholarships")} className="btn btn-sm btn-outline" style={{ fontSize: 11.5, padding: "3px 8px" }}>
                                        All Aid <FiArrowRight size={11} />
                                    </Link>
                                </div>

                                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem", marginBottom: 10 }}>
                                    <div style={{ padding: "0.6rem", background: "#f8fafc", borderRadius: 8, border: "1px solid #e2e8f0" }}>
                                        <div style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 700 }}>Total Sanctioned</div>
                                        <div style={{ fontSize: 18, fontWeight: 800, color: "#7c3aed", marginTop: 2 }}>{inrShort(scholarships.totalSanctioned || 0)}</div>
                                    </div>
                                    <div style={{ padding: "0.6rem", background: "#f8fafc", borderRadius: 8, border: "1px solid #e2e8f0" }}>
                                        <div style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 700 }}>Disbursed</div>
                                        <div style={{ fontSize: 18, fontWeight: 800, color: "#059669", marginTop: 2 }}>{inrShort(scholarships.totalDisbursed || 0)}</div>
                                    </div>
                                </div>

                                <div style={{ fontSize: 12, color: "#475569", display: "flex", justifyContent: "space-between", borderTop: "1px solid #f1f5f9", paddingTop: 8 }}>
                                    <span>Beneficiary Students: <strong>{scholarships.studentCount || 0}</strong></span>
                                    <span>Pending: <strong style={{ color: "#d97706" }}>{inrShort(scholarships.totalPending || 0)}</strong></span>
                                </div>
                            </div>

                            {/* Hostel & Transport Overview Card */}
                            <div className="card" style={{ padding: "1.25rem", borderRadius: 10, border: "1px solid #e2e8f0" }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
                                        <span style={{ width: 30, height: 30, borderRadius: 8, background: "#d9770615", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                            <FiTruck size={16} color="#d97706" />
                                        </span>
                                        <div>
                                            <div style={{ fontWeight: 700, fontSize: 14 }}>Hostel &amp; Transport Services</div>
                                            <div style={{ fontSize: 11.5, color: "#64748b" }}>Campus facilities infrastructure</div>
                                        </div>
                                    </div>
                                    <Link to={withCollege("/transport")} className="btn btn-sm btn-outline" style={{ fontSize: 11.5, padding: "3px 8px" }}>
                                        Details <FiArrowRight size={11} />
                                    </Link>
                                </div>

                                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem", marginBottom: 10 }}>
                                    <div style={{ padding: "0.6rem", background: "#f8fafc", borderRadius: 8, border: "1px solid #e2e8f0" }}>
                                        <div style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 700 }}>Hostel Occupancy</div>
                                        <div style={{ fontSize: 18, fontWeight: 800, color: "#003b67", marginTop: 2 }}>{hostel.occupancyPct || 0}%</div>
                                        <div style={{ fontSize: 10.5, color: "#64748b" }}>{hostel.occupiedBeds || 0} of {hostel.totalCapacity || 0} beds</div>
                                    </div>
                                    <div style={{ padding: "0.6rem", background: "#f8fafc", borderRadius: 8, border: "1px solid #e2e8f0" }}>
                                        <div style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 700 }}>Bus Passengers</div>
                                        <div style={{ fontSize: 18, fontWeight: 800, color: "#d97706", marginTop: 2 }}>{transport.activeSubscribers || 0}</div>
                                        <div style={{ fontSize: 10.5, color: "#64748b" }}>across {transport.routesCount || 0} routes</div>
                                    </div>
                                </div>

                                <div style={{ fontSize: 12, color: "#475569", display: "flex", justifyContent: "space-between", borderTop: "1px solid #f1f5f9", paddingTop: 8 }}>
                                    <span>Bus Fees Collected: <strong>{inrShort(transport.totalCollected || 0)}</strong></span>
                                    <span>Monthly: <strong>{inrShort(transport.monthlyReceivable || 0)}</strong></span>
                                </div>
                            </div>
                        </div>

                        {/* ── College Escalations & Resource Requisitions Panel ── */}
                        <div className="card" style={{ marginBottom: "1.5rem", borderRadius: 10, overflow: "hidden", border: "1px solid #e2e8f0" }}>
                            <div style={{
                                padding: "1rem 1.25rem",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                flexWrap: "wrap",
                                gap: "0.75rem",
                                backgroundColor: "#fafbfc",
                                borderBottom: "1px solid #f1f5f9",
                            }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
                                    <span style={{
                                        width: 32, height: 32, borderRadius: 8, background: "#6366f118",
                                        display: "flex", alignItems: "center", justifyContent: "center", color: "#6366f1",
                                    }}>
                                        <FiLayers size={17} />
                                    </span>
                                    <div>
                                        <div style={{ fontSize: 14, fontWeight: 700 }}>
                                            College Escalations &amp; Resource Requisitions
                                        </div>
                                        <div style={{ fontSize: 11.5, color: "var(--text-muted)" }}>
                                            Matters escalated by college principals or requests for equipment &amp; resources
                                        </div>
                                    </div>
                                </div>

                                <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                                    {escalationStats?.urgent > 0 && (
                                        <span style={{
                                            fontSize: 11, fontWeight: 700, backgroundColor: "#fee2e2",
                                            color: "#991b1b", padding: "3px 8px", borderRadius: 99,
                                        }}>
                                            {escalationStats.urgent} URGENT
                                        </span>
                                    )}
                                    <button
                                        className="btn btn-outline"
                                        style={{ fontSize: 12, padding: "5px 10px" }}
                                        onClick={() => navigate(withCollege("/escalations"))}
                                    >
                                        Manage All Requests <FiArrowRight size={13} />
                                    </button>
                                </div>
                            </div>

                            {/* Stat sub-counters */}
                            <div style={{
                                display: "grid",
                                gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                                gap: "0.75rem",
                                padding: "1rem 1.25rem 0.5rem",
                            }}>
                                <div style={{ padding: "0.6rem 0.8rem", borderRadius: 8, background: "#fff7ed", border: "1px solid #ffedd5" }}>
                                    <div style={{ fontSize: 11, color: "#9a3412", fontWeight: 700, textTransform: "uppercase" }}>Pending Review</div>
                                    <div style={{ fontSize: 18, fontWeight: 800, color: "#7c2d12" }}>{escalationStats?.pending || 0}</div>
                                </div>
                                <div style={{ padding: "0.6rem 0.8rem", borderRadius: 8, background: "#e8f1f8", border: "1px solid #e8f1f8" }}>
                                    <div style={{ fontSize: 11, color: "#1e40af", fontWeight: 700, textTransform: "uppercase" }}>Matter Escalations</div>
                                    <div style={{ fontSize: 18, fontWeight: 800, color: "#1e3a8a" }}>{escalationStats?.escalations || 0}</div>
                                </div>
                                <div style={{ padding: "0.6rem 0.8rem", borderRadius: 8, background: "#f5f3ff", border: "1px solid #ede9fe" }}>
                                    <div style={{ fontSize: 11, color: "#6b21a8", fontWeight: 700, textTransform: "uppercase" }}>Resource Requests</div>
                                    <div style={{ fontSize: 18, fontWeight: 800, color: "#581c87" }}>{escalationStats?.resourceRequests || 0}</div>
                                </div>
                                <div style={{ padding: "0.6rem 0.8rem", borderRadius: 8, background: "#ecfdf5", border: "1px solid #d1fae5" }}>
                                    <div style={{ fontSize: 11, color: "#065f46", fontWeight: 700, textTransform: "uppercase" }}>Resolved / Approved</div>
                                    <div style={{ fontSize: 18, fontWeight: 800, color: "#064e3b" }}>{escalationStats?.resolved || 0}</div>
                                </div>
                            </div>

                            {/* Pending Items List */}
                            <div style={{ padding: "0.75rem 1.25rem 1.25rem" }}>
                                {pendingEscalations.length === 0 ? (
                                    <div style={{ padding: "1.25rem", textAlign: "center", color: "#64748b", fontSize: 13, background: "#f8fafc", borderRadius: 8 }}>
                                        No pending college escalations or resource requisitions requiring review.
                                    </div>
                                ) : (
                                    <div className="table-responsive">
                                        <table className="table" style={{ width: "100%", margin: 0 }}>
                                            <thead>
                                                <tr style={{ fontSize: 12 }}>
                                                    <th>Ticket</th>
                                                    <th>College</th>
                                                    <th>Type &amp; Category</th>
                                                    <th>Subject</th>
                                                    <th>Priority</th>
                                                    <th style={{ textAlign: "right" }}>Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {pendingEscalations.map((p) => (
                                                    <tr key={p._id}>
                                                        <td style={{ fontFamily: "monospace", fontWeight: 700, fontSize: 12 }}>{p.ticketNumber}</td>
                                                        <td style={{ fontWeight: 600, fontSize: 12.5 }}>{p.college?.code || p.college?.name || "College"}</td>
                                                        <td>
                                                            <span style={{
                                                                fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", padding: "2px 6px", borderRadius: 4,
                                                                background: p.type === "escalation" ? "#fef3c7" : "#e0e7ff",
                                                                color: p.type === "escalation" ? "#92400e" : "#3730a3",
                                                            }}>
                                                                {p.type === "escalation" ? "Escalation" : "Resource"}
                                                            </span>
                                                            <span style={{ fontSize: 11, color: "#64748b", marginLeft: 6 }}>{p.category}</span>
                                                        </td>
                                                        <td style={{ fontWeight: 600, fontSize: 13 }}>{p.title}</td>
                                                        <td>
                                                            <span style={{
                                                                fontSize: 10, fontWeight: 700, textTransform: "uppercase", padding: "2px 6px", borderRadius: 4,
                                                                background: p.priority === "urgent" ? "#fee2e2" : p.priority === "high" ? "#ffedd5" : "#e0f2fe",
                                                                color: p.priority === "urgent" ? "#991b1b" : p.priority === "high" ? "#9a3412" : "#0369a1",
                                                            }}>
                                                                {p.priority}
                                                            </span>
                                                        </td>
                                                        <td style={{ textAlign: "right" }}>
                                                            <button
                                                                className="btn btn-sm btn-primary"
                                                                onClick={() => navigate(`/escalations/${p._id}`)}
                                                                style={{ fontSize: 11.5, padding: "3px 8px" }}
                                                            >
                                                                Review &amp; Act
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* ── Quick Action Management Hub ──────────────────────── */}
                        <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", marginBottom: "2rem" }}>
                            <button className="btn btn-primary" onClick={() => handleOpenCreateAdmin()} style={{ background: "#059669", borderColor: "#059669" }}>
                                <FiUserPlus size={13} /> Create College Admin
                            </button>
                            <button className="btn btn-primary" onClick={() => navigate(withCollege("/communications"))}>
                                <FiSend size={13} /> Group Communications
                            </button>
                            <button className="btn btn-outline" onClick={() => navigate(withCollege("/superadmin/cash-radar"))}>
                                <FiActivity size={13} color="#003b67" /> Campus Cash Radar
                            </button>
                            <button className="btn btn-outline" onClick={() => navigate("/colleges")}>
                                <FiHome size={13} /> Manage Colleges
                            </button>
                            <button className="btn btn-outline" onClick={() => navigate("/colleges/new")}>
                                <FiUserPlus size={13} /> Add College
                            </button>
                            <button className="btn btn-outline" onClick={() => navigate(withCollege("/users"))}>
                                <FiUsers size={13} /> User Accounts
                            </button>
                            <button className="btn btn-outline" onClick={() => navigate("/settings")}>
                                <FiSettings size={13} /> Global Settings
                            </button>
                        </div>
                    </>
                )}
            </div>

            {/* Create College Administrator Modal */}
            <CreateCollegeAdminModal
                isOpen={adminModalOpen}
                onClose={() => setAdminModalOpen(false)}
                colleges={colleges}
                preSelectedCollegeId={preSelectedAdminCol}
                onSuccess={loadDashboard}
            />
        </div>
    );
}

// ── Create College Administrator Modal Component ─────────────────────────────
function CreateCollegeAdminModal({ isOpen, onClose, colleges = [], preSelectedCollegeId, onSuccess }) {
    const [collegeId, setCollegeId] = useState("");
    const [displayName, setDisplayName] = useState("");
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [createdResult, setCreatedResult] = useState(null);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        const targetId = preSelectedCollegeId || (colleges[0] ? colleges[0]._id : "");
        setCollegeId(targetId);
        setCreatedResult(null);
        setCopied(false);
    }, [isOpen, preSelectedCollegeId, colleges]);

    const generatePassword = (colCode = "CMS") => {
        const prefix = colCode.toUpperCase().replace(/[^A-Z]/g, "") || "ADMIN";
        const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%";
        let randStr = "";
        for (let i = 0; i < 3; i++) randStr += chars.charAt(Math.floor(Math.random() * chars.length));
        const num = Math.floor(100 + Math.random() * 900);
        return `${prefix}@${num}${randStr}`;
    };

    useEffect(() => {
        if (collegeId && colleges.length > 0) {
            const col = colleges.find((c) => String(c._id) === String(collegeId));
            if (col) {
                const codeSlug = col.code.toLowerCase().replace(/[^a-z0-9]/g, "");
                setUsername(`${codeSlug}_admin`);
                setDisplayName(`Administrator — ${col.name}`);
                setPassword(generatePassword(col.code));
            }
        }
    }, [collegeId, colleges]);

    if (!isOpen) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!collegeId) {
            toast.error("Please select a target college campus.");
            return;
        }
        if (!username.trim()) {
            toast.error("Admin username is required.");
            return;
        }
        if (!password || password.length < 6) {
            toast.error("Password must be at least 6 characters.");
            return;
        }

        setSubmitting(true);
        try {
            const selectedCol = colleges.find((c) => String(c._id) === String(collegeId));
            const payload = {
                username: username.trim().toLowerCase(),
                password,
                displayName: displayName.trim() || `Administrator — ${selectedCol?.name || "College"}`,
                role: "admin",
                college: collegeId,
            };

            const res = await userService.create(payload);
            toast.success(res.message || "College Admin account created successfully!");

            const loginUrl = `${window.location.origin}/login?college=${selectedCol?.code || ""}`;

            setCreatedResult({
                collegeName: selectedCol?.name || "College",
                collegeCode: selectedCol?.code || "",
                username: username.trim().toLowerCase(),
                password,
                loginUrl,
            });

            if (onSuccess) onSuccess();
        } catch (err) {
            toast.error(err.response?.data?.message || err.message || "Failed to create Admin user.");
        } finally {
            setSubmitting(false);
        }
    };

    const handleCopyCredentials = () => {
        if (!createdResult) return;
        const text = `Central India Group of Institutions\n` +
            `College: ${createdResult.collegeName} (${createdResult.collegeCode})\n` +
            `Role: College Administrator\n` +
            `Username: ${createdResult.username}\n` +
            `Password: ${createdResult.password}\n` +
            `Portal Login URL: ${createdResult.loginUrl}\n`;

        navigator.clipboard.writeText(text);
        setCopied(true);
        toast.success("Credentials copied to clipboard!");
        setTimeout(() => setCopied(false), 2500);
    };

    return (
        <div style={{
            position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
            background: "rgba(15, 23, 42, 0.65)", backdropFilter: "blur(4px)",
            zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center",
            padding: "1rem"
        }}>
            <div style={{
                background: "#ffffff", borderRadius: 14, width: "100%", maxWidth: 520,
                boxShadow: "0 20px 40px rgba(0,0,0,0.2)", overflow: "hidden", border: "1px solid #e2e8f0"
            }}>
                {/* Header */}
                <div style={{
                    padding: "1.1rem 1.5rem", background: "#0f172a", color: "#ffffff",
                    display: "flex", alignItems: "center", justifyContent: "space-between"
                }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <span style={{
                            width: 34, height: 34, borderRadius: 8,
                            background: "rgba(176,141,69,0.25)", color: "#f7d583",
                            display: "flex", alignItems: "center", justifyContent: "center"
                        }}>
                            <FiShield size={18} />
                        </span>
                        <div>
                            <div style={{ fontWeight: 800, fontSize: 15 }}>
                                {createdResult ? "Admin Account Created Successfully" : "Create College Administrator"}
                            </div>
                            <div style={{ fontSize: 11.5, color: "#94a3b8" }}>
                                {createdResult ? "Credentials ready for handover" : "Assign primary administrative credentials to a college campus"}
                            </div>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        style={{ background: "none", border: "none", color: "#94a3b8", cursor: "pointer", padding: 4 }}
                    >
                        <FiX size={18} />
                    </button>
                </div>

                {/* Body */}
                <div style={{ padding: "1.5rem" }}>
                    {createdResult ? (
                        <div>
                            <div style={{
                                padding: "0.9rem 1rem", borderRadius: 10, background: "#f0fdf4",
                                border: "1px solid #bbf7d0", marginBottom: "1.25rem"
                            }}>
                                <div style={{ fontWeight: 800, fontSize: 13.5, color: "#166534", marginBottom: 3, display: "flex", alignItems: "center", gap: 6 }}>
                                    <FiCheck size={16} /> Ready for Campus Handover
                                </div>
                                <div style={{ fontSize: 12, color: "#15803d" }}>
                                    The administrator for <strong>{createdResult.collegeName}</strong> can now log in and manage campus records and staff accounts.
                                </div>
                            </div>

                            <div style={{
                                background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10,
                                padding: "1rem 1.25rem", display: "grid", gap: "0.75rem", fontSize: 13, marginBottom: "1.5rem"
                            }}>
                                <div>
                                    <span style={{ color: "#64748b", fontSize: 11, textTransform: "uppercase", fontWeight: 700, display: "block" }}>Campus</span>
                                    <strong style={{ color: "#0f172a" }}>{createdResult.collegeName} ({createdResult.collegeCode})</strong>
                                </div>
                                <div>
                                    <span style={{ color: "#64748b", fontSize: 11, textTransform: "uppercase", fontWeight: 700, display: "block" }}>Admin Username</span>
                                    <code style={{ fontSize: 13, fontWeight: 700, background: "#e2e8f0", padding: "3px 8px", borderRadius: 4, color: "#0f172a" }}>
                                        {createdResult.username}
                                    </code>
                                </div>
                                <div>
                                    <span style={{ color: "#64748b", fontSize: 11, textTransform: "uppercase", fontWeight: 700, display: "block" }}>Password</span>
                                    <code style={{ fontSize: 13, fontWeight: 700, background: "#fee2e2", padding: "3px 8px", borderRadius: 4, color: "#991b1b" }}>
                                        {createdResult.password}
                                    </code>
                                </div>
                                <div>
                                    <span style={{ color: "#64748b", fontSize: 11, textTransform: "uppercase", fontWeight: 700, display: "block" }}>Dedicated College Portal URL</span>
                                    <a href={createdResult.loginUrl} target="_blank" rel="noreferrer" style={{ color: "#003b67", fontWeight: 600, fontSize: 12, wordBreak: "break-all" }}>
                                        {createdResult.loginUrl}
                                    </a>
                                </div>
                            </div>

                            <div style={{ display: "flex", gap: "0.75rem" }}>
                                <button
                                    type="button"
                                    onClick={handleCopyCredentials}
                                    className="btn btn-primary"
                                    style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "9px" }}
                                >
                                    {copied ? <><FiCheck size={14} /> Copied to Clipboard!</> : <><FiCopy size={14} /> Copy All Credentials</>}
                                </button>
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="btn btn-outline"
                                    style={{ padding: "9px 16px" }}
                                >
                                    Done
                                </button>
                            </div>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit}>
                            {/* Campus Selector */}
                            <div className="form-group" style={{ marginBottom: "1rem" }}>
                                <label className="form-label" style={{ fontSize: 12, fontWeight: 700 }}>
                                    Target College Campus <span style={{ color: "#dc2626" }}>*</span>
                                </label>
                                <select
                                    className="form-control"
                                    value={collegeId}
                                    onChange={(e) => setCollegeId(e.target.value)}
                                    style={{ fontSize: 13, fontWeight: 600, color: "#003b67" }}
                                >
                                    <option value="">Select College Campus…</option>
                                    {colleges.map((c) => (
                                        <option key={c._id} value={c._id}>
                                            {c.name} ({c.code}) — {c.discipline || "General"}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Display Name */}
                            <div className="form-group" style={{ marginBottom: "1rem" }}>
                                <label className="form-label" style={{ fontSize: 12, fontWeight: 700 }}>
                                    Administrator Display Name
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={displayName}
                                    onChange={(e) => setDisplayName(e.target.value)}
                                    placeholder="e.g. Administrator — CICP"
                                />
                            </div>

                            {/* Username */}
                            <div className="form-group" style={{ marginBottom: "1rem" }}>
                                <label className="form-label" style={{ fontSize: 12, fontWeight: 700 }}>
                                    Admin Username <span style={{ color: "#dc2626" }}>*</span>
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value.toLowerCase().trim())}
                                    placeholder="e.g. cicop_admin"
                                />
                                <span className="form-hint" style={{ fontSize: 11, color: "#94a3b8", marginTop: 3, display: "block" }}>
                                    Login handle for the college portal
                                </span>
                            </div>

                            {/* Password */}
                            <div className="form-group" style={{ marginBottom: "1.5rem" }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                                    <label className="form-label" style={{ fontSize: 12, fontWeight: 700, margin: 0 }}>
                                        Password <span style={{ color: "#dc2626" }}>*</span>
                                    </label>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const col = colleges.find((c) => String(c._id) === String(collegeId));
                                            setPassword(generatePassword(col?.code || "CMS"));
                                        }}
                                        style={{
                                            background: "none", border: "none", color: "#003b67",
                                            fontSize: 11.5, fontWeight: 700, cursor: "pointer", padding: 0
                                        }}
                                    >
                                        ⚡ Generate Password
                                    </button>
                                </div>
                                <div style={{ position: "relative" }}>
                                    <input
                                        type={showPw ? "text" : "password"}
                                        className="form-control"
                                        style={{ paddingRight: 40 }}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder="Minimum 6 characters"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPw((p) => !p)}
                                        style={{
                                            position: "absolute", right: 10, top: "50%",
                                            transform: "translateY(-50%)", background: "none",
                                            border: "none", color: "#64748b", cursor: "pointer", padding: 4
                                        }}
                                    >
                                        {showPw ? <FiEyeOff size={15} /> : <FiEye size={15} />}
                                    </button>
                                </div>
                            </div>

                            {/* Actions */}
                            <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.75rem" }}>
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="btn btn-outline"
                                    style={{ padding: "8px 14px" }}
                                    disabled={submitting}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    style={{ padding: "8px 18px", background: "#059669", borderColor: "#059669" }}
                                    disabled={submitting}
                                >
                                    {submitting ? "Creating Admin Account…" : "Create Administrator Account"}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}